import { useEffect, useRef, useState } from 'react'
import { searchUsers } from '../api/userAdmin'
import { useDebouncedValue } from '../lib/useDebouncedValue'
import { Icon } from './icons/Icon'
import { ApiError } from '../api/problem'
import type { UserSearchResult } from '../types/userAdmin'

export interface UserPickerValue {
  id: string
  name: string
  email: string
}

export interface UserPickerProps {
  id: string
  label: string
  value: UserPickerValue | null
  onChange: (value: UserPickerValue | null) => void
  placeholder?: string
  /** Surfaced under the input for a11y context (e.g. "Search users by name or email…"). */
  helpText?: string
}

/**
 * IB-328/IB-336 — a reusable, accessible user-directory typeahead over `GET /api/v1/users/search`,
 * mirroring `AccountLookupField`'s combobox pattern exactly (debounced query, listbox popover,
 * click-outside close). Replaces every raw-UUID "User ID" text field this codebase shipped before
 * the reverse-lookup port existed (Case Team's `AddTeamMemberModal`/`ReassignTeamMemberModal` being
 * the named gap the design calls out) — the FIRST reuse point; a second consumer (Case Owner
 * assignment) demonstrates the picker is genuinely shared, not a one-off.
 *
 * Selecting a result only ever produces a `{id, name, email}` value for the caller to submit — this
 * component never performs the assignment mutation itself, so server-side authorization (e.g.
 * `case:W`) is untouched: the picker narrows WHO you can pick from the directory, never WHAT you're
 * allowed to do with the pick.
 */
export function UserPicker({ id, label, value, onChange, placeholder, helpText }: UserPickerProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [results, setResults] = useState<UserSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const debouncedQuery = useDebouncedValue(query, 250)

  useEffect(() => {
    if (!open || debouncedQuery.trim().length < 1) {
      setResults([])
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    searchUsers(debouncedQuery, 10)
      .then((matches) => {
        if (cancelled) return
        setResults(matches)
      })
      .catch((err) => {
        if (cancelled) return
        setResults([])
        setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not search users.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [debouncedQuery, open])

  useEffect(() => {
    if (!open) return
    function onClickOutside(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  if (value) {
    return (
      <div className="iw-field">
        <span className="iw-field-label" id={`${id}-label`}>
          {label}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
          <span className="iw-pill iw-pill--neutral">
            {value.name} <span style={{ color: 'var(--iw-text-muted)' }}>· {value.email}</span>
          </span>
          <button type="button" className="iw-btn iw-btn--sm" onClick={() => onChange(null)}>
            Clear
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="iw-field" ref={containerRef} style={{ position: 'relative' }}>
      <label className="iw-field-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-autocomplete="list"
        aria-describedby={helpText ? `${id}-help` : undefined}
        className="iw-input"
        placeholder={placeholder ?? 'Search by name or email…'}
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
      />
      {helpText ? (
        <span id={`${id}-help`} className="iw-visually-hidden">
          {helpText}
        </span>
      ) : null}
      {open && (loading || results.length > 0 || error || query.trim().length > 0) ? (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          aria-label={`${label} results`}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 'var(--iw-z-dropdown)' as never,
            background: 'var(--iw-surface-raised)',
            border: '1px solid var(--iw-border)',
            borderRadius: 'var(--iw-radius)',
            boxShadow: 'var(--iw-shadow-lg)',
            margin: '4px 0 0',
            padding: 4,
            listStyle: 'none',
            maxHeight: 220,
            overflowY: 'auto',
          }}
        >
          {loading ? (
            <li style={{ padding: 8, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Searching…</li>
          ) : error ? (
            <li style={{ padding: 8, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-danger)' }}>{error}</li>
          ) : results.length === 0 ? (
            <li style={{ padding: 8, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>No matches</li>
          ) : (
            results.map((user) => (
              <li key={user.id} role="option" aria-selected="false">
                <button
                  type="button"
                  className="iw-btn iw-btn--ghost iw-btn--sm"
                  style={{ width: '100%', justifyContent: 'flex-start', gap: 'var(--iw-space-2)' }}
                  onClick={() => {
                    onChange({ id: user.id, name: user.name, email: user.email })
                    setQuery('')
                    setOpen(false)
                  }}
                >
                  <Icon name="user" size={13} />
                  <span>
                    {user.name} <span style={{ color: 'var(--iw-text-muted)' }}>· {user.email}</span>
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  )
}
